import { useSelector } from 'react-redux';
import { Page } from '@aldebaran/common/types/variant';
import { RootState } from '../types/graph';

const hasProgressSteps = (pages: Record<string, Page>): boolean => {
  return Object.values(pages).every(
    (page) => page.progressSteps && page.progressSteps.length > 0,
  );
};

const calculateProgressWithCustomSteps = (
  currentPage: string,
  currentQuestionIndex: number,
  pages: Record<string, Page>,
): number => {
  const page = pages[currentPage];
  if (!page?.progressSteps?.length) return 0;

  const index = Math.min(currentQuestionIndex, page.progressSteps.length - 1);
  return page.progressSteps[index];
};

const calculateProgressWithDefaultSteps = (
  currentPage: string,
  currentQuestionIndex: number,
  pages: Record<string, Page>,
): number => {
  const totalNumberOfPages = Object.keys(pages).length;
  if (totalNumberOfPages === 0) {
    return 0;
  }

  const currentPageIndex = Object.keys(pages).indexOf(currentPage);
  if (currentPageIndex === -1) {
    return 0;
  }
  const pagePercentage = 100 / totalNumberOfPages;

  return Math.round(
    Math.min(
      currentPageIndex * pagePercentage +
        currentQuestionIndex * (pagePercentage / 10),
      (currentPageIndex + 1) * pagePercentage,
    ),
  );
};

export const useProgressCalculation = (): number => {
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const currentQuestionIndex = useSelector(
    (state: RootState) => state.questionnaire.currentQuestionIndex,
  );
  const pages = useSelector(
    (state: RootState) => state.questionnaire.config?.pages,
  );

  if (!pages) {
    return 0;
  }

  if (hasProgressSteps(pages)) {
    return calculateProgressWithCustomSteps(
      currentPage,
      currentQuestionIndex,
      pages,
    );
  }

  return calculateProgressWithDefaultSteps(
    currentPage,
    currentQuestionIndex,
    pages,
  );
};
